import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { handleApiError } from "@/utils/errorHandling";
import { customToast } from "@components/Common/Toast";
import { fetchEmployeeInformation } from "@/services/summary";
import {
  SummaryBeneficiary,
  SummaryGoal,
  ContributionData,
  SplitData,
  PlanData,
  SummaryData,
  UseSummaryDataReturn,
} from "@/types/summary";

interface EmployeeInfoRecord {
  minorAccountID: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  relationship: string;
  dateOfBirth: string;
  minorEnrolledStatus: string;
  goal?: string;
  amountRequired?: number;
  timeline?: string;
  employeeMinorContributionInPercent?: number;
  employerMinorContributionInPercent?: number;
  employeeMinorContributionInDollar?: number;
  employerMinorContributionInDollar?: number;
  investmentOption?: string;
  employeeTotalContributionInPercent?: number;
  employeeTotalContributionInDollar?: number;
  employerTotalContributionInPercent?: number;
  employerTotalContributionInDollar?: number;
}

export const useSummaryData = (): UseSummaryDataReturn => {
  const [data, setData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { id: userAccountID, employeeID } = useSelector(
    (state: RootState) => state.user,
  );

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!employeeID || !userAccountID) {
        setError("User information not available");
        setLoading(false);
        return;
      }

      const employeeInfoResponse = await fetchEmployeeInformation(
        employeeID,
        userAccountID.toString(),
      );

      const employeeData: EmployeeInfoRecord[] =
        employeeInfoResponse.data || [];

      const beneficiariesMap = new Map<string, SummaryBeneficiary>();
      const goalsMap = new Map<string, SummaryGoal>();
      const splitDataMap = new Map<string, SplitData>();
      const planDataMap = new Map<string, PlanData>();

      const contribution: ContributionData | undefined =
        employeeData.length > 0
          ? {
              employeeTotalContribution:
                employeeData[0].employeeTotalContributionInPercent || 0,
              employeeTotalContributionInDollar:
                employeeData[0].employeeTotalContributionInDollar || 0,
              employerTotalContribution:
                employeeData[0].employerTotalContributionInPercent || 0,
              employerTotalContributionInDollar:
                employeeData[0].employerTotalContributionInDollar || 0,
            }
          : undefined;

      employeeData.forEach((record) => {
        const beneficiaryName = `${record.firstName} ${record.lastName}`;

        if (!beneficiariesMap.has(record.minorAccountID)) {
          beneficiariesMap.set(record.minorAccountID, {
            minorAccountID: record.minorAccountID,
            firstName: record.firstName,
            lastName: record.lastName,
            middleName: record.middleName || "",
            relationship: record.relationship,
            dateOfBirth: record.dateOfBirth,
            onboardingProgress: record.minorEnrolledStatus,
          });
        }

        if (
          record.goal &&
          record.amountRequired &&
          !goalsMap.has(record.minorAccountID)
        ) {
          goalsMap.set(record.minorAccountID, {
            minorAccountID: record.minorAccountID,
            goalType: record.goal!,
            amountRequired: record.amountRequired!,
            timeline: record.timeline ?? "",
            fullName: beneficiaryName,
            amount: record.amountRequired,
            purpose: record.goal,
          });
        }

        if (!splitDataMap.has(record.minorAccountID)) {
          splitDataMap.set(record.minorAccountID, {
            minorAccountID: record.minorAccountID,
            beneficiaryName,
            employeePercentage: record.employeeMinorContributionInPercent || 0,
            employerPercentage: record.employerMinorContributionInPercent || 0,
            employeeDollarAmount: record.employeeMinorContributionInDollar || 0,
            employerDollarAmount: record.employerMinorContributionInDollar || 0,
          });
        }

        if (
          record.investmentOption &&
          !planDataMap.has(record.minorAccountID)
        ) {
          planDataMap.set(record.minorAccountID, {
            minorAccountID: record.minorAccountID,
            beneficiaryName,
            investmentOption: record.investmentOption,
            academicTargetYear: record.timeline
              ? new Date(record.timeline).getFullYear().toString()
              : undefined,
            planType: "custom", // This could be enhanced to determine recommendation vs custom
          });
        }
      });

      const beneficiaries: SummaryBeneficiary[] = Array.from(
        beneficiariesMap.values(),
      );
      const goals: SummaryGoal[] = Array.from(goalsMap.values());
      const splitData: SplitData[] = Array.from(splitDataMap.values());
      const planData: PlanData[] = Array.from(planDataMap.values());

      setData({
        beneficiaries,
        goals,
        contribution,
        split: splitData,
        plan: planData,
      });
    } catch (err: unknown) {
      handleApiError(err, "Unable to load summary data", false, customToast);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userAccountID, employeeID]);

  const refetch = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (userAccountID && employeeID) {
      fetchAllData();
    } else {
      setError("User information not available");
      setLoading(false);
    }
  }, [fetchAllData, userAccountID, employeeID]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};
